import { useState, useEffect, useCallback } from "react";

// ─── TYPES & DATA ─────────────────────────────────────────────────────────────

type Category = "all" | "collection" | "bot" | "monad" | "market" | "community" | "lore";

interface Fact {
  id: number;
  icon: string;
  cat: Exclude<Category, "all">;
  text: string;
}

const ALL_FACTS: Fact[] = [
  { id: 1,  icon: "🔢", cat: "collection", text: "The 10K Squad holds only 3,333 NFTs — not 10,000. The name is aspirational, symbolising community strength and a goal to reach a 10,000 MON floor, not supply count." },
  { id: 2,  icon: "📅", cat: "collection", text: "The collection launched on Monad mainnet in December 2025, riding the wave of Monad's public debut as a high-performance EVM L1 blockchain." },
  { id: 3,  icon: "🧮", cat: "collection", text: "3,333 is exactly one-third of 10,000 — the name implies only 1/3 of the world carries the full squad firepower. Intentional gate-keeping energy." },
  { id: 4,  icon: "🖼", cat: "collection", text: "Listed under the 'Art' category on OpenSea at opensea.io/collection/the-10k-squad-350905768, positioning it as a pure culture piece, not a utility play." },
  { id: 5,  icon: "💎", cat: "collection", text: "Each NFT is built from 6–8 unique trait layers: background, body, eyes, headwear, mouth, accessories, and special effects — no two are identical." },
  { id: 6,  icon: "🛡", cat: "collection", text: "Because the art lives on a decentralised L1, it cannot be removed by a centralised server or corporate decision — censorship resistance baked into the protocol." },
  { id: 7,  icon: "🎨", cat: "collection", text: "Art files are high-resolution (likely 4000×4000 px), made affordable by Monad's near-zero gas costs — rich metadata without bloat fees." },
  { id: 8,  icon: "🌱", cat: "collection", text: "The project gained serious traction during the Monad Testnet phase — one testnet whale wallet reportedly controlled 10% of the testnet supply at peak activity." },
  { id: 9,  icon: "🏛", cat: "collection", text: "The team uses a Squads multisig for treasury management, delegating upgrade authority to a transparent Squad address visible to the whole community." },
  { id: 10, icon: "🔒", cat: "collection", text: "The smart contract is renounced or locked via Squads, making any future unauthorised minting impossible. The 3,333 supply is final and immutable." },
  { id: 11, icon: "🦅", cat: "lore",       text: "The mascot is a stylish avian figure typically seen in a hoodie or futuristic gear. The community has unofficially named it 'Squadron'." },
  { id: 12, icon: "🌟", cat: "lore",       text: "The Legendary variant is rendered in solid gold with diamond eyes — the rarest visual configuration in the entire 3,333-piece collection." },
  { id: 13, icon: "🌌", cat: "lore",       text: "The rarest background depicts a tear in space-time known as 'The Rift' — a Legendary-tier trait that commands a serious secondary market premium." },
  { id: 14, icon: "💻", cat: "lore",       text: "The Matrix Code background is Epic rarity, featuring cascading green symbols — a nod to hacker culture that resonates deeply with the Monad dev community." },
  { id: 15, icon: "🔥", cat: "lore",       text: "The 'Glitch' eyes are a fan-favourite for rarity snipers — flickering, corrupted pixels that scream 1/1 energy even in a 3,333-supply collection." },
  { id: 16, icon: "🚬", cat: "lore",       text: "The Cigarette mouth trait has become the aesthetic signature of the collection — rare, recognisable, pure vaporwave-meets-crypto in a single pixel placement." },
  { id: 17, icon: "😇", cat: "lore",       text: "The Angelic Halo is the highest-tier headwear, embodying the collection's love of contradiction: celestial aesthetics in a chaotic on-chain world." },
  { id: 18, icon: "🤝", cat: "lore",       text: "A known crossover trait features LFJ (Liquid Finance Jupiter) branding — real cross-ecosystem collaboration baked directly into art metadata." },
  { id: 19, icon: "💡", cat: "lore",       text: "Background elements are speculated to have AI-assisted generation, but the core character was hand-drawn — a human touch underneath the on-chain hype." },
  { id: 20, icon: "🌀", cat: "lore",       text: "'Broken Halo' sits at Epic rarity — fallen-angel aesthetics and crypto-degen energy perfectly merged into a single headwear trait." },
  { id: 21, icon: "🔮", cat: "lore",       text: "Community speculation is brewing around a future Pixel variant side-collection — a pixel-art callback that would complement the main collection's slick visuals." },
  { id: 22, icon: "👥", cat: "community",  text: "There are 1,142 unique holders, representing a 34.3% unique ownership rate — remarkably healthy distribution for an NFT collection of this size and age." },
  { id: 23, icon: "📊", cat: "community",  text: "The top 10 wallets hold less than 15% of total supply — far more distributed than most PFP projects, signalling genuine community ownership." },
  { id: 24, icon: "🔑", cat: "community",  text: "Only 2.6% of supply is listed for sale at any given time. This extreme low float signals holders have strong conviction and aren't eager to exit." },
  { id: 25, icon: "🏰", cat: "community",  text: "The Discord server is wallet-gated. Holding 1 NFT unlocks access — and grants entry to community-only alpha channels unavailable to the public." },
  { id: 26, icon: "🫂", cat: "community",  text: "Holders call themselves 'Squad Members.' No official title system — just the quiet pride of being part of a tight-knit, Monad-native community." },
  { id: 27, icon: "🚫", cat: "community",  text: "The team deliberately avoids roadmap promises. No utility farming, no hype cycles — just organic art value and culture that grows on its own merit." },
  { id: 28, icon: "⚡", cat: "community",  text: "The team drops random, unannounced wallet snapshots for giveaways. No warning. Just hold — and you might get rewarded out of nowhere." },
  { id: 29, icon: "📷", cat: "community",  text: "'Vibes > Utility' — the collection's unofficial motto. A deliberate counter-movement to the broken promises of 2021–2024 NFT roadmap culture." },
  { id: 30, icon: "🌐", cat: "community",  text: "Monad ecosystem projects have airdropped tokens to 10K Squad holders, rewarding early participants for backing the ecosystem from the very beginning." },
  { id: 31, icon: "🏆", cat: "community",  text: "The community views The 10K Squad as the CryptoPunks of Monad — the original blue-chip PFP setting the cultural benchmark for everything that follows." },
  { id: 32, icon: "🤝", cat: "community",  text: "Despite being led by @0xaquar, the community operates with a 'Community Takeover' (CTO) mentality — members treat the brand as their own to protect and grow." },
  { id: 33, icon: "🌊", cat: "community",  text: "Monad restaking participants have reportedly received whitelist spots for 10K Squad mints — rewarding deep ecosystem believers with early access." },
  { id: 34, icon: "🪙", cat: "market",     text: "Total trading volume is approximately 668,400 MON — substantial liquidity for a 3,333-piece collection on a chain that only went live in late 2025." },
  { id: 35, icon: "📈", cat: "market",     text: "On its record day, the floor price moved +50.8% in a single 24-hour window — the kind of explosive action that made early holders very happy." },
  { id: 36, icon: "💰", cat: "market",     text: "The highest standing buy offer sits at 1,455 MON — dangerously close to the floor, signalling real demand from buyers unwilling to wait for sellers." },
  { id: 37, icon: "📉", cat: "market",     text: "During certain post-launch windows, the collection saw 73%+ price appreciation — cementing its status as one of the top-performing Monad NFTs of early 2026." },
  { id: 38, icon: "🔄", cat: "market",     text: "A SQUAD/WMON trading pair exists on Uniswap V3 deployed on Monad, offering DeFi-native exposure to the brand without holding an NFT directly." },
  { id: 39, icon: "💸", cat: "market",     text: "Liquidity Providers on the SQUAD/WMON pair earn fees from traders swapping in and out — a passive income layer orbiting the NFT collection's brand value." },
  { id: 40, icon: "🎯", cat: "market",     text: "Creator royalties are enforced on-chain, ensuring the team earns on every secondary sale — aligning long-term incentives between creators and the community." },
  { id: 41, icon: "🧐", cat: "market",     text: "Rarity sniping is a common strategy — buyers specifically hunt undervalued Epic background NFTs listed below their intrinsic rarity premium." },
  { id: 42, icon: "🌉", cat: "market",     text: "Many early buyers bridged ETH from Ethereum mainnet to Monad specifically to participate in the collection mint — bootstrapping real activity on a brand-new chain." },
  { id: 43, icon: "📱", cat: "market",     text: "The collection is listed on multiple Monad aggregators beyond OpenSea, giving it broader exposure and deeper liquidity across the ecosystem." },
  { id: 44, icon: "⛽", cat: "market",     text: "During the December 2025 mint, gas spiked to $0.05 — historically extreme for Monad — causing community-wide shock from users used to paying under $0.001." },
  { id: 45, icon: "🤖", cat: "bot",        text: "@10ksquadbot is the official real-time sales tracker for The 10K Squad, built and maintained by @0xaquar on X (formerly Twitter)." },
  { id: 46, icon: "📡", cat: "bot",        text: "The bot fires Discord webhooks within milliseconds of a confirmed sale — faster than OpenSea's own indexing catches up. First-mover information advantage." },
  { id: 47, icon: "📣", cat: "bot",        text: "Every notification follows the format: 'Squad #[Number] just sold for X MON' — clean, consistent, zero noise, immediately actionable." },
  { id: 48, icon: "⏰", cat: "bot",        text: "The @10ksquadbot account was created in April 2026, deployed as the collection began gaining serious secondary market traction." },
  { id: 49, icon: "🌍", cat: "bot",        text: "All posts are in English, making data accessible to the globally distributed Monad NFT community across every timezone." },
  { id: 50, icon: "🖼", cat: "bot",        text: "The bot's avatar uses a cropped version of the official 10K Squad character art — maintaining brand consistency across every alert it fires." },
  { id: 51, icon: "🔍", cat: "bot",        text: "The bot flags suspicious wallet-to-wallet transactions, helping the community identify potential wash trading and distinguish real volume from artificial inflation." },
  { id: 52, icon: "📊", cat: "bot",        text: "Running on serverless functions, the bot maintains an estimated 99.9%+ uptime — it doesn't sleep, doesn't take breaks, and never misses a sale." },
  { id: 53, icon: "👁", cat: "bot",        text: "Holders use the bot to track floor movements in real time without refreshing marketplace UIs — information delivered to you, not hunted down by you." },
  { id: 54, icon: "🎯", cat: "bot",        text: "The long-term goal of @10ksquadbot is to reach 10,000 followers — mirroring the aspirational '10K' in the collection's own name." },
  { id: 55, icon: "📢", cat: "bot",        text: "The bot auto-retweets whale flips and record-breaking sales, amplifying major transactions to its full follower base for maximum community visibility." },
  { id: 56, icon: "⛓", cat: "monad",      text: "Monad is a standalone Layer 1 blockchain — not an Ethereum Layer 2 like Arbitrum or Optimism. It runs its own consensus and does not settle to Ethereum." },
  { id: 57, icon: "🔄", cat: "monad",      text: "Monad achieves 10,000 TPS via parallel execution. Ethereum mainnet handles roughly 15 TPS. That is a 666× speed improvement — not a typo." },
  { id: 58, icon: "🔗", cat: "monad",      text: "Monad is fully EVM-compatible — any Ethereum developer can redeploy their smart contracts to Monad with zero code changes required." },
  { id: 59, icon: "⚡", cat: "monad",      text: "Block finality on Monad is near-instant, often sub-second. Minting an NFT feels like pressing a button and watching it appear in your wallet immediately." },
  { id: 60, icon: "💵", cat: "monad",      text: "Gas fees on Monad are typically under $0.001 per transaction. The December spike to $0.05 was considered a crisis — that's how low the baseline normally is." },
  { id: 61, icon: "📖", cat: "monad",      text: "The name 'Monad' comes from Leibnizian philosophy, referring to an ultimate, indivisible unit of reality — with a nod to functional programming's Monad pattern." },
  { id: 62, icon: "🏗", cat: "monad",      text: "The Monad testnet phase saw explosive community growth as users competed to earn points, test dApps, and establish early ecosystem presence before mainnet." },
];

const CAT_META: Record<Exclude<Category, "all">, { label: string; color: string; bg: string; border: string }> = {
  collection: { label: "COLLECTION", color: "#ffd700", bg: "rgba(255,215,0,0.08)",   border: "rgba(255,215,0,0.25)" },
  bot:        { label: "BOT",        color: "#67e8f9", bg: "rgba(103,232,249,0.08)", border: "rgba(103,232,249,0.25)" },
  monad:      { label: "MONAD",      color: "#c084fc", bg: "rgba(192,132,252,0.08)", border: "rgba(192,132,252,0.25)" },
  market:     { label: "MARKET",     color: "#4ade80", bg: "rgba(74,222,128,0.08)",  border: "rgba(74,222,128,0.25)" },
  community:  { label: "COMMUNITY",  color: "#fb923c", bg: "rgba(251,146,60,0.08)",  border: "rgba(251,146,60,0.25)" },
  lore:       { label: "LORE",       color: "#ff2680", bg: "rgba(255,38,128,0.08)",  border: "rgba(255,38,128,0.3)" },
};

const RARITY_TIERS = [
  { label: "Common",    pct: 42, color: "#ff80b0" },
  { label: "Uncommon",  pct: 28, color: "#ff2680" },
  { label: "Rare",      pct: 15, color: "#c084fc" },
  { label: "Epic",      pct: 10, color: "#818cf8" },
  { label: "Legendary", pct: 5,  color: "#ffd700" },
];

const TRAIT_GROUPS = [
  { name: "🖼 Background", tiers: ["Solid Color", "Gradient Wash", "Neon Cityscape", "Matrix Code", "The Rift"] },
  { name: "👁 Eyes",       tiers: ["Standard White", "Shades", "Hollow", "Glitch", "Laser / Flame"] },
  { name: "👕 Body",       tiers: ["Classic Hoodie", "Trench Coat", "Elder Robe", "Mech Suit", "Gold Armor"] },
  { name: "🎩 Headwear",   tiers: ["Hood Up", "Baseball Cap", "Headphones", "Broken Halo", "Angelic Halo"] },
  { name: "👄 Mouth",      tiers: ["Smirk", "Mask", "Cigarette", "Open Beak", "Gold Grill"] },
  { name: "✨ Special",    tiers: ["None", "Aura", "Glow Effect", "LFJ Crossover", "Diamond"] },
];

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function Facts() {
  const [activeFilter, setActiveFilter] = useState<Category>("all");
  const [randomFact, setRandomFact]     = useState<Fact | null>(null);
  const [isFlipping, setIsFlipping]     = useState(false);
  const [mounted, setMounted]           = useState(false);

  useEffect(() => { setMounted(true); }, []);

  const filtered = activeFilter === "all"
    ? ALL_FACTS
    : ALL_FACTS.filter(f => f.cat === activeFilter);

  const handleRandom = useCallback(() => {
    if (isFlipping) return;
    setIsFlipping(true);
    setTimeout(() => {
      setRandomFact(ALL_FACTS[Math.floor(Math.random() * ALL_FACTS.length)]);
      setIsFlipping(false);
    }, 200);
  }, [isFlipping]);

  const tabs: { key: Category; label: string }[] = [
    { key: "all",        label: "ALL" },
    { key: "collection", label: "COLLECTION" },
    { key: "bot",        label: "BOT" },
    { key: "monad",      label: "MONAD" },
    { key: "market",     label: "MARKET" },
    { key: "community",  label: "COMMUNITY" },
    { key: "lore",       label: "LORE" },
  ];

  return (
    <div style={{ background: "#0a0008", minHeight: "100vh", fontFamily: "'DM Sans', sans-serif", color: "#fff", position: "relative" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }

        .facts-grid-bg {
          position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background-image:
            linear-gradient(rgba(255,38,128,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,38,128,0.04) 1px, transparent 1px);
          background-size: 44px 44px;
        }

        .f-hero-title {
          font-family: 'Bebas Neue', sans-serif;
          font-size: clamp(72px, 14vw, 130px);
          line-height: 0.88;
          letter-spacing: 0.04em;
          color: #fff;
        }

        .f-sec-title {
          font-family: 'Bebas Neue', sans-serif;
          font-size: clamp(32px, 5vw, 52px);
          letter-spacing: 0.06em;
          line-height: 1;
          color: #fff;
        }

        .f-mono { font-family: 'JetBrains Mono', monospace; }
        .f-display { font-family: 'Bebas Neue', sans-serif; }

        .stat-pill {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,38,128,0.18);
          border-radius: 14px;
          padding: 14px 20px;
          text-align: center;
          min-width: 110px;
          transition: transform 0.15s, border-color 0.15s;
        }
        .stat-pill:hover { transform: translateY(-3px); border-color: rgba(255,215,0,0.35); }

        .btn-rng {
          background: #ff2680;
          color: #fff;
          border: none;
          font-family: 'Bebas Neue', sans-serif;
          font-size: 22px;
          letter-spacing: 0.12em;
          padding: 16px 44px;
          border-radius: 14px;
          cursor: pointer;
          box-shadow: 0 4px 24px rgba(255,38,128,0.35);
          transition: background 0.18s, transform 0.1s, box-shadow 0.18s;
        }
        .btn-rng:hover { background: #e0005c; box-shadow: 0 8px 32px rgba(255,38,128,0.5); }
        .btn-rng:active { transform: scale(0.97); }

        .rnd-card {
          background: rgba(255,255,255,0.03);
          border: 1.5px solid rgba(255,215,0,0.25);
          border-radius: 20px;
          padding: 30px 30px 24px;
          max-width: 640px;
          margin: 26px auto 0;
          text-align: left;
          transition: opacity 0.18s, transform 0.18s;
        }
        .rnd-card.flip { opacity: 0; transform: scale(0.97) translateY(8px); }

        .tab-btn {
          padding: 7px 18px;
          border-radius: 30px;
          font-size: 12px;
          font-family: 'JetBrains Mono', monospace;
          cursor: pointer;
          border: 1px solid rgba(255,38,128,0.22);
          background: transparent;
          color: rgba(255,180,210,0.65);
          transition: all 0.18s;
          letter-spacing: 0.05em;
        }
        .tab-btn:hover { border-color: #ff2680; color: #ff2680; background: rgba(255,38,128,0.07); }
        .tab-btn.active { background: #ff2680; color: #fff; border-color: #ff2680; }

        .fact-card {
          background: rgba(255,255,255,0.025);
          border: 1px solid rgba(255,38,128,0.1);
          border-radius: 16px;
          padding: 20px 20px 16px;
          transition: border-color 0.18s, transform 0.2s, box-shadow 0.2s, background 0.18s;
          animation: fcIn 0.28s ease both;
        }
        .fact-card:hover {
          border-color: rgba(255,38,128,0.35);
          transform: translateY(-3px);
          box-shadow: 0 12px 32px rgba(255,38,128,0.07);
          background: rgba(255,38,128,0.035);
        }
        @keyframes fcIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .fc-badge {
          display: inline-block;
          font-size: 10px;
          font-family: 'JetBrains Mono', monospace;
          padding: 3px 10px;
          border-radius: 20px;
          letter-spacing: 0.07em;
          border: 1px solid transparent;
          font-weight: 500;
        }

        .rarity-card {
          background: rgba(255,255,255,0.025);
          border: 1px solid rgba(255,38,128,0.1);
          border-radius: 16px;
          padding: 22px;
        }
        .rbar-track {
          height: 6px;
          background: rgba(255,255,255,0.06);
          border-radius: 10px;
          overflow: hidden;
        }
        .rbar-fill { height: 100%; border-radius: 10px; transition: width 0.7s cubic-bezier(.4,0,.2,1); }

        .monad-stat {
          background: rgba(192,132,252,0.06);
          border: 1px solid rgba(192,132,252,0.18);
          border-radius: 16px;
          padding: 20px;
          text-align: center;
          transition: transform 0.15s;
        }
        .monad-stat:hover { transform: translateY(-3px); }

        .bot-card {
          background: rgba(103,232,249,0.04);
          border: 1px solid rgba(103,232,249,0.18);
          border-radius: 20px;
          padding: 30px;
        }
        .bot-avatar {
          width: 72px; height: 72px; border-radius: 50%;
          background: linear-gradient(135deg, #ff2680, #c084fc);
          display: flex; align-items: center; justify-content: center;
          font-size: 32px; flex-shrink: 0;
        }
        .bot-chip {
          background: rgba(103,232,249,0.07);
          border: 1px solid rgba(103,232,249,0.18);
          border-radius: 10px;
          padding: 10px 16px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          color: #67e8f9;
        }
        .bot-chip strong {
          display: block;
          font-family: 'Bebas Neue', sans-serif;
          font-size: 22px;
          letter-spacing: 0.06em;
        }
        .btn-follow {
          display: inline-block;
          background: #1d9bf0;
          color: #fff;
          font-family: 'Bebas Neue', sans-serif;
          font-size: 17px;
          letter-spacing: 0.1em;
          padding: 11px 26px;
          border-radius: 12px;
          text-decoration: none;
          transition: background 0.18s;
          border: none; cursor: pointer;
        }
        .btn-follow:hover { background: #1a8cd8; }

        .pink-div {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,38,128,0.28), transparent);
          margin: 56px 0; border: none;
        }

        .facts-footer {
          border-top: 1px solid rgba(255,38,128,0.1);
          padding: 36px 20px 60px;
          text-align: center;
          margin-top: 40px;
        }

        @media (max-width: 640px) {
          .f-hero-title { font-size: 64px; }
          .bot-inner { flex-direction: column !important; }
        }
      `}</style>

      <div className="facts-grid-bg" />

      {/* Ambient orbs */}
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,38,128,0.1) 0%, transparent 70%)", top: -150, right: -150 }} />
        <div style={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(192,132,252,0.08) 0%, transparent 70%)", bottom: 80, left: -120 }} />
        <div style={{ position: "absolute", width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,215,0,0.05) 0%, transparent 70%)", top: "38%", left: "35%" }} />
      </div>

      <div style={{ maxWidth: 1140, margin: "0 auto", padding: "0 20px", position: "relative", zIndex: 1 }}>

        {/* ── HERO ────────────────────────────────────── */}
        <div style={{ textAlign: "center", padding: "72px 0 48px" }}>
          <div className="f-mono" style={{
            display: "inline-block", fontSize: 11,
            color: "#ffd700",
            border: "1px solid rgba(255,215,0,0.3)",
            padding: "5px 16px", borderRadius: 20, marginBottom: 28,
            letterSpacing: "0.14em", background: "rgba(255,215,0,0.06)", fontWeight: 500,
          }}>
            LORE HUB · MONAD MAINNET · EST. DEC 2025
          </div>

          <h1 className="f-hero-title">
            <span style={{ color: "#ffd700" }}>10K </span>
            <span style={{ color: "#ff2680" }}>FACTS</span>
          </h1>

          <p style={{
            fontSize: "clamp(15px, 2vw, 17px)",
            color: "rgba(255,180,210,0.65)",
            maxWidth: 520,
            margin: "20px auto 0",
            lineHeight: 1.65,
          }}>
            Everything you didn't know about The 10K Squad — lore, rarity, market data, and the bot behind it all.{" "}
            <span className="f-mono" style={{ color: "#67e8f9", fontSize: 13 }}>
              {ALL_FACTS.length} facts.
            </span>
          </p>
        </div>

        {/* ── STAT BAR ────────────────────────────────── */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginBottom: 60 }}>
          {[
            { val: "3,333",  lbl: "TOTAL SUPPLY" },
            { val: "1,142",  lbl: "UNIQUE HOLDERS" },
            { val: "668K",   lbl: "MON VOLUME" },
            { val: "+50.8%", lbl: "BEST 24H MOVE" },
            { val: "2.6%",   lbl: "SUPPLY LISTED" },
            { val: "34.3%",  lbl: "UNIQUE OWNERSHIP" },
          ].map(s => (
            <div className="stat-pill" key={s.lbl}>
              <div className="f-display" style={{ fontSize: 30, lineHeight: 1, color: "#ffd700" }}>{s.val}</div>
              <div className="f-mono" style={{ fontSize: 10, color: "rgba(255,180,210,0.55)", marginTop: 4, letterSpacing: "0.08em" }}>{s.lbl}</div>
            </div>
          ))}
        </div>

        {/* ── RANDOM FACT ─────────────────────────────── */}
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <p className="f-mono" style={{ fontSize: 11, color: "rgba(255,215,0,0.35)", letterSpacing: "0.1em", marginBottom: 18 }}>
            // HIT THE BUTTON — GET A RANDOM FACT
          </p>
          <button className="btn-rng" onClick={handleRandom}>⚡ GENERATE RANDOM FACT</button>

          {randomFact && (
            <div className={`rnd-card${isFlipping ? " flip" : ""}`}>
              <div className="f-mono" style={{ fontSize: 11, color: "#ffd700", letterSpacing: "0.12em", marginBottom: 10 }}>
                FACT #{String(randomFact.id).padStart(3, "0")}
              </div>
              <div style={{ fontSize: 38, marginBottom: 10, lineHeight: 1 }}>{randomFact.icon}</div>
              <div style={{ fontSize: 17, lineHeight: 1.7, color: "#fff", fontWeight: 500 }}>{randomFact.text}</div>
              <div style={{ marginTop: 18 }}>
                <span className="fc-badge" style={{
                  background: CAT_META[randomFact.cat].bg,
                  color: CAT_META[randomFact.cat].color,
                  borderColor: CAT_META[randomFact.cat].border,
                }}>
                  {CAT_META[randomFact.cat].label}
                </span>
              </div>
            </div>
          )}
        </div>

        <hr className="pink-div" />

        {/* ── FILTER TABS ─────────────────────────────── */}
        <div style={{ marginBottom: 28 }}>
          <p className="f-mono" style={{ fontSize: 11, color: "rgba(255,38,128,0.38)", letterSpacing: "0.08em", marginBottom: 14 }}>
            // FILTER BY CATEGORY
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {tabs.map(t => (
              <button
                key={t.key}
                className={`tab-btn${activeFilter === t.key ? " active" : ""}`}
                onClick={() => setActiveFilter(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── SECTION HEAD ────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 24 }}>
          <h2 className="f-sec-title">
            DID YOU <span style={{ color: "#ff2680" }}>KNOW?</span>
          </h2>
          <span className="f-mono" style={{ fontSize: 12, color: "rgba(255,38,128,0.38)" }}>
            {filtered.length} facts
          </span>
        </div>

        {/* ── FACTS GRID ──────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(295px, 1fr))", gap: 16, marginBottom: 64 }}>
          {filtered.map((f, i) => {
            const meta = CAT_META[f.cat];
            return (
              <div className="fact-card" key={f.id} style={{ animationDelay: `${Math.min(i * 28, 280)}ms` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <span style={{ fontSize: 24, lineHeight: 1 }}>{f.icon}</span>
                  <span className="f-mono" style={{ fontSize: 10, color: "rgba(255,38,128,0.38)", letterSpacing: "0.1em" }}>
                    FACT #{String(f.id).padStart(3, "0")}
                  </span>
                </div>
                <p style={{ fontSize: 14, lineHeight: 1.72, color: "rgba(255,235,245,0.82)", marginBottom: 14 }}>
                  {f.text}
                </p>
                <span className="fc-badge" style={{ background: meta.bg, color: meta.color, borderColor: meta.border }}>
                  {meta.label}
                </span>
              </div>
            );
          })}
        </div>

        <hr className="pink-div" />

        {/* ── RARITY BREAKDOWN ────────────────────────── */}
        <div style={{ marginBottom: 64 }}>
          <h2 className="f-sec-title" style={{ marginBottom: 8 }}>
            RARITY <span style={{ color: "#ffd700" }}>BREAKDOWN</span>
          </h2>
          <p style={{ fontSize: 14, color: "rgba(255,180,210,0.5)", marginBottom: 24 }}>
            Trait distribution across all 3,333 pieces — from Common to Legendary.
          </p>

          {/* Legend */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 28 }}>
            {RARITY_TIERS.map(t => (
              <div key={t.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: 3, background: t.color, flexShrink: 0 }} />
                <span className="f-mono" style={{ fontSize: 11, color: "rgba(255,220,240,0.6)" }}>
                  {t.label} — {t.pct}%
                </span>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
            {TRAIT_GROUPS.map(g => (
              <div className="rarity-card" key={g.name}>
                <div className="f-display" style={{ fontSize: 20, letterSpacing: "0.08em", color: "#ffd700", marginBottom: 16 }}>
                  {g.name}
                </div>
                {RARITY_TIERS.map((t, i) => (
                  <div key={t.label} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                      <span className="f-mono" style={{ fontSize: 11, color: "rgba(255,220,240,0.65)" }}>
                        {t.label} — {g.tiers[i]}
                      </span>
                      <span className="f-mono" style={{ fontSize: 11, color: t.color, fontWeight: 500 }}>{t.pct}%</span>
                    </div>
                    <div className="rbar-track">
                      <div className="rbar-fill" style={{ width: mounted ? `${t.pct}%` : "0%", background: t.color }} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <hr className="pink-div" />

        {/* ── MONAD ───────────────────────────────────── */}
        <div style={{ marginBottom: 64 }}>
          <h2 className="f-sec-title" style={{ marginBottom: 8 }}>
            WHY <span style={{ color: "#c084fc" }}>MONAD?</span>
          </h2>
          <p style={{ fontSize: 14, color: "rgba(192,132,252,0.5)", marginBottom: 28 }}>
            The chain that makes 10K Squad possible — blazing fast, absurdly cheap, fully EVM-compatible.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 14 }}>
            {[
              { val: "10K",     lbl: "TPS",             sub: "vs ETH's ~15 TPS" },
              { val: "~1s",     lbl: "FINALITY",        sub: "Near-instant settlement" },
              { val: "<$0.001", lbl: "GAS FEE",         sub: "Fractions of a cent" },
              { val: "666×",    lbl: "FASTER THAN ETH", sub: "Parallel execution" },
              { val: "EVM",     lbl: "COMPATIBLE",      sub: "Zero code migration" },
              { val: "L1",      lbl: "STANDALONE",      sub: "Not a rollup, not an L2" },
            ].map(m => (
              <div className="monad-stat" key={m.lbl}>
                <div className="f-display" style={{ fontSize: 36, color: "#c084fc", lineHeight: 1 }}>{m.val}</div>
                <div className="f-mono" style={{ fontSize: 10, color: "rgba(192,132,252,0.65)", letterSpacing: "0.08em", marginTop: 4 }}>{m.lbl}</div>
                <div style={{ fontSize: 12, color: "rgba(192,132,252,0.4)", marginTop: 3 }}>{m.sub}</div>
              </div>
            ))}
          </div>
        </div>

        <hr className="pink-div" />

        {/* ── BOT ─────────────────────────────────────── */}
        <div style={{ marginBottom: 64 }}>
          <h2 className="f-sec-title" style={{ marginBottom: 24 }}>
            THE <span style={{ color: "#67e8f9" }}>BOT</span>
          </h2>
          <div className="bot-card">
            <div className="bot-inner" style={{ display: "flex", gap: 26, flexWrap: "wrap", alignItems: "flex-start" }}>
              <div className="bot-avatar">🤖</div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div className="f-display" style={{ fontSize: 30, letterSpacing: "0.08em", color: "#67e8f9", marginBottom: 8 }}>
                  @10KSQUADBOT
                </div>
                <p style={{ fontSize: 15, color: "rgba(180,240,255,0.72)", lineHeight: 1.65 }}>
                  The real-time sales tracker for The 10K Squad. Every flip on Monad hits X and Discord simultaneously — no marketplace refreshing required. Built and maintained by{" "}
                  <span style={{ color: "#ffd700", fontWeight: 600 }}>@0xaquar</span> since April 2026.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 20 }}>
                  {[
                    { val: "~ms",   lbl: "Discord latency" },
                    { val: "99.9%", lbl: "Uptime" },
                    { val: "24/7",  lbl: "Monitoring" },
                    { val: "EN",    lbl: "Language" },
                  ].map(c => (
                    <div className="bot-chip" key={c.lbl}>
                      <strong>{c.val}</strong>
                      {c.lbl}
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 22 }}>
                  <a className="btn-follow" href="https://x.com/10ksquadbot" target="_blank" rel="noopener noreferrer">
                    🐦 FOLLOW @10KSQUADBOT
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── FOOTER ──────────────────────────────────── */}
      <div className="facts-footer">
        <p className="f-mono" style={{ fontSize: 12, color: "rgba(255,38,128,0.3)", letterSpacing: "0.08em" }}>
          THE 10K SQUAD · MONAD MAINNET · DECEMBER 2025 · PURE CULTURE, ZERO ROADMAP
        </p>
        <p style={{ marginTop: 10 }}>
          <a
            href="https://opensea.io/collection/the-10k-squad-350905768"
            target="_blank"
            rel="noopener noreferrer"
            className="f-mono"
            style={{ color: "#ff2680", textDecoration: "none", fontSize: 12 }}
          >
            opensea.io/collection/the-10k-squad-350905768
          </a>
        </p>
      </div>
    </div>
  );
}